import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { EASE } from "../../lib/motion";
import { STORY_TEXTURES } from "../../data/site";

const LINES = [
  [{ t: "NOT LUCK.", c: "paper" }],
  [{ t: "METHOD.", c: "yellow" }],
];

export const StoryHero = () => {
  const scrollToOrigin = () => {
    const el = document.querySelector("[data-testid='story-origin']");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="story-hero"
      className="bg-deepred pt-24 pb-10 sm:pt-28"
    >
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
        {/* Top meta rail */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="mb-5 flex items-center justify-between"
        >
          <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-labyellow">
            OUR STORY // NEW JERSEY
          </span>
          <span className="hidden font-mono text-[11px] uppercase tracking-[0.35em] text-paper/50 sm:block">
            ENGINEERED BY HAND
          </span>
        </motion.div>

        {/* Banner card */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="relative overflow-hidden rounded-[28px] bg-labred"
        >
          <div className="grid grid-cols-1 md:grid-cols-[1.05fr_0.95fr]">
            {/* Left — copy */}
            <div className="relative z-10 flex flex-col justify-center px-7 py-12 sm:px-12 sm:py-16 lg:px-16 lg:py-20">
              <span className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-paper/30 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-paper/80">
                <span className="h-1.5 w-1.5 rounded-full bg-labyellow" />
                The Formula for Flavor
              </span>

              <h1
                className="font-display text-4xl leading-[0.98] tracking-tight sm:text-5xl lg:text-6xl"
                data-testid="story-hero-headline"
              >
                {LINES.map((line, li) => (
                  <span key={li} className="block overflow-hidden">
                    <motion.span
                      className="block"
                      initial={{ y: "110%" }}
                      animate={{ y: "0%" }}
                      transition={{ delay: 0.3 + li * 0.13, duration: 0.85, ease: EASE }}
                    >
                      {line.map((seg, si) => (
                        <span key={si} className={seg.c === "yellow" ? "text-labyellow" : "text-paper"}>
                          {seg.t}
                        </span>
                      ))}
                    </motion.span>
                  </span>
                ))}
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.75, duration: 0.6 }}
                className="mt-6 max-w-md font-body text-base leading-relaxed text-paper/90"
                data-testid="story-hero-subhead"
              >
                For us, flavor is not a formula stumbled upon in the kitchen. It is a discovery
                process, controlled from the first ingredient to the final crunch.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.6 }}
                className="mt-9 flex flex-wrap items-center gap-6"
              >
                <Link
                  to="/menu"
                  data-testid="story-hero-menu-button"
                  className="inline-flex items-center justify-center rounded-full bg-paper px-8 py-3.5 font-display text-xs uppercase tracking-wider text-inkred transition-colors duration-150 hover:bg-labyellow"
                >
                  See the Menu
                </Link>
                <button
                  onClick={scrollToOrigin}
                  data-testid="story-hero-read-link"
                  className="group inline-flex items-center gap-2 font-body text-sm font-medium text-paper transition-colors hover:text-labyellow"
                >
                  Read the full story
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </button>
              </motion.div>
            </div>

            {/* Right — food image */}
            <div className="relative min-h-[280px] overflow-hidden md:min-h-[540px]">
              <motion.img
                src={STORY_TEXTURES.whyLabb}
                alt="Chicken Labb signature fried chicken basket"
                initial={{ scale: 1.12 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
